<template>
  <div class="tw-max-w-[800px] lg:tw-w-[800px] tw-w-auto">
    <ContentHeaderComponent
      :title="
        getComponentUiText(
          'app-menu-submenu-home-setup-wizard-modal-goals-title-1',
        )
      "
      :sub-title="
        getComponentUiText(
          'app-menu-submenu-home-setup-wizard-modal-goals-sub-title-1',
        )
      "
    />
    <div
      v-if="isApiError && props.errorMessage"
      class="tw-bg-[#FEE5E2] tw-px-[12px] tw-py-[8px] tw-mx-[25px] tw-flex tw-items-center tw-mt-[25px] tw-rounded-[6px] tw-gap-[10px]"
    >
      <WarningIcon color="#F7553C" />
      <p
        class="tw-text-[#F7553C] tw-text-[11px] tw-font-[500] tw-leading-[130%] tw-text-center tw-mb-[0px]"
      >
        {{
          getComponentUiText(
            "app-component-wizards-setup-error-tooltip",
            false,
            {
              errorMessage: props.errorMessage,
            },
          )
        }}
      </p>
    </div>
    <div
      class="tw-flex tw-leading-[100%] tw-justify-between tw-gap-[20px] tw-bg-[#ffffff] tw-rounded-b-md tw-border-[1px] tw-border-t-0 tw-border-solid tw-border-[#EFF0F1] tw-p-[25px]"
    >
      <div class="tw-flex tw-flex-col tw-w-[100%] tw-gap-[20px]">
        <b-row
          v-for="(question, index) in questionData"
          :key="index"
          :class="'row-container tw-flex tw-flex-row tw-gap-[40px] tw-mr-0 tw-ml-0'"
        >
          <b-col :class="'tw-pr-0 tw-pl-0 tw-flex tw-items-center'">
            <label
              class="tw-mb-0 tw-text-[13px] tw-text-[#231F20] tw-leading-[145%] tw-font-medium tw-font-Poppins"
              >{{ question.title }}</label
            >
          </b-col>

          <b-col :class="'col-sm-6 tw-pr-0 tw-pl-0'">
            <div class="dropdown">
              <BaseDropDown
                :id="'goals-dropdown-' + index"
                :model-value="
                  question.values.length > 0
                    ? question.values[question.values.length - 1][
                        question.valueId
                      ]
                    : []
                "
                :data="question.QuestionOptions || []"
                :validation="{}"
                :border-select="true"
                :class-name="'gray-dropdown-container tw-h-[56px]'"
                :class="`${
                  !Boolean(question.values[question.values.length - 1]?.id) &&
                  apiError &&
                  question.values.length !== 0
                    ? 'error'
                    : ''
                }`"
                @update:model-value="($event) => selectValue($event, question)"
              >
              </BaseDropDown>
            </div>
          </b-col>
        </b-row>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { getComponentUiText } from "@/utils/utilities";
import ContentHeaderComponent from "./contentHeaderComponent.vue";
import BaseDropDown from "../common/form/baseDropDown.vue";
import store from "@/store";
import {
  WIZARD_STATE_NAME,
  WIZARD_STATE_QUESTION_TYPE_ID,
} from "@/utils/constants";
import { generateQuestionDataPage } from "@/services/wizard";
import { IDropDownData } from "@/interfaces/common/IDropDown";
import { IWizardPageQuestion } from "@/interfaces/wizard";
import WarningIcon from "../svgs/warningIcon.vue";

const props = defineProps({
  isApiError: {
    type: Boolean,
    required: false,
    default: false,
  },
  errorMessage: {
    type: String,
    required: false,
    default: "",
  },
});

const apiError = ref(false);

watch(
  () => props.isApiError,
  (newValue) => {
    apiError.value = newValue;
  },
  { immediate: true },
);

const goalsPageQuestion: IWizardPageQuestion[] = [
  {
    id: WIZARD_STATE_QUESTION_TYPE_ID.CONCERN,
    valueId: WIZARD_STATE_QUESTION_TYPE_ID.CONCERN + "Id",
    title: getComponentUiText(
      "app-menu-submenu-home-setup-wizard-modal-goals-questions-concern-title-1",
    ),
    typeId: WIZARD_STATE_QUESTION_TYPE_ID.CONCERN,
    QuestionOptions: [] as IDropDownData[],
    values: [{ [WIZARD_STATE_QUESTION_TYPE_ID.CONCERN + "Id"]: "" }],
  },
  {
    id: WIZARD_STATE_QUESTION_TYPE_ID.FEATURE,
    valueId: WIZARD_STATE_QUESTION_TYPE_ID.FEATURE + "Id",
    title: getComponentUiText(
      "app-menu-submenu-home-setup-wizard-modal-goals-questions-feature-title-1",
    ),
    typeId: WIZARD_STATE_QUESTION_TYPE_ID.FEATURE,
    QuestionOptions: [] as IDropDownData[],
    values: [{ [WIZARD_STATE_QUESTION_TYPE_ID.FEATURE + "Id"]: "" }],
  },
  {
    id: WIZARD_STATE_QUESTION_TYPE_ID.GOAL,
    valueId: WIZARD_STATE_QUESTION_TYPE_ID.GOAL + "Id",
    title: getComponentUiText(
      "app-menu-submenu-home-setup-wizard-modal-goals-questions-goal-title-1",
    ),
    typeId: WIZARD_STATE_QUESTION_TYPE_ID.GOAL,
    QuestionOptions: [] as IDropDownData[],
    values: [{ [WIZARD_STATE_QUESTION_TYPE_ID.GOAL + "Id"]: "" }],
  },
];

const currentOrg = computed(() => store.state.Org.currentOrg);

const questionSectionValue = computed(() =>
  store.getters["SetupWizard/getQuestionsBySection"](
    WIZARD_STATE_NAME.GOALS,
    currentOrg.value.id || null,
  ),
);

const questionData = computed(() =>
  generateQuestionDataPage(goalsPageQuestion, questionSectionValue.value),
);

const emits = defineEmits(["check:select"]);

const selectValue = (value, question: IWizardPageQuestion) => {
  apiError.value = false;
  store.dispatch("SetupWizard/updateQuestions", {
    value: { [question.valueId]: value },
    orgId: currentOrg.value.id || null,
    typeId: question.typeId,
    page: WIZARD_STATE_NAME.GOALS,
  });

  store.commit("SetupWizard/SET_SELECT", value);
  emits("check:select", value);
};
</script>

<style lang="scss" scoped>
:deep(.dropdown-menu) {
  right: 0px;
  left: auto !important;
  width: max-content !important;
}
:deep(.dropdown-toggle) {
  max-width: 374px;
}
.dropdown-toggle.show .right-icon-arrow {
  transition: transform 0.3s ease;
  transform: rotate(-180deg);
  height: 12px;
}
.dropdown-toggle .right-icon-arrow {
  transition: transform 0.3s ease;
  transform: rotate(0deg);
  height: 12px;
}

.error {
  :deep(.baseDropDown.gray-dropdown-container .btn-secondary) {
    border: 1px solid red !important;
  }
}

@media screen and (max-width: 600px) {
  .row-container {
    flex-direction: column;
    gap: 20px;

    .col-sm-6 {
      max-width: 100%;
    }
  }
}
</style>
